/**
 * Merges the user ids selected in the form with the previously-saved assigned
 * users for a single approver role, preserving each existing user's extra
 * fields (e.g. approvalComment/approved) and tracking which user ids are
 * brand new (so they can be notified). Any form user id without a match in
 * `originalAssignedUsers` is submitted as `{ userId }` only.
 */
const mergeAssignedUsers = (formUserIds, originalAssignedUsers, newlyAddedUserIds) =>
  (formUserIds ?? []).map((userId) => {
    const existingUser = (originalAssignedUsers ?? []).find(
      (u) => u.userId === userId
    );

    if (!existingUser) newlyAddedUserIds.push(userId);

    return existingUser || { userId };
  });

/**
 * Builds the `approvers` array (and the list of newly-added user ids to
 * notify) for the milestone update payload from the "Replace approvers" form.
 * Named approvers (role shaped, with a `regionId`) are upserted in place;
 * the single "additional approvers" entry (no `regionId`, ad-hoc users with
 * no role) is rebuilt from the `additionalApprovers` form field. This is the
 * shape persisted by `PATCH /projects/{projectId}/milestone` - see
 * UpdateProjectMilestoneCommandHandler on the server.
 */
export const buildUpdatedApprovers = (approvers, formValues) => {
  const newApproversToNotify = [];
  const safeApprovers = approvers ?? [];
  const safeFormValues = formValues ?? {};

  const originalAdditionalApprovers =
    safeApprovers.find((x) => !x.regionId) || {};
  const additionalApproversRole = {
    ...originalAdditionalApprovers,
    assignedUsers: mergeAssignedUsers(
      (safeFormValues.additionalApprovers ?? []).slice().sort(),
      originalAdditionalApprovers.assignedUsers,
      newApproversToNotify
    ),
  };

  const namedApprovers = safeApprovers
    .filter((x) => x.regionId)
    .map((approver) => {
      const formData = safeFormValues[approver.id];
      if (!formData) return approver;

      return {
        ...approver,
        isNotApplicable: formData.isNotApplicable ?? approver.isNotApplicable,
        assignedUsers: mergeAssignedUsers(
          (formData.assignedUsers ?? []).slice().sort(),
          approver.assignedUsers,
          newApproversToNotify
        ),
      };
    });

  return {
    approvers: [...namedApprovers, additionalApproversRole],
    newApproversToNotify: [...new Set(newApproversToNotify)],
  };
};
